/**
 * Audit Logs Page - Admin Only
 * Full-featured audit trail viewer with filtering, stats, detail modal, CSV export
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  Download,
  RefreshCw,
  AlertTriangle,
  Shield,
  Clock,
  Activity,
  ChevronDown,
  X,
  Eye,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText,
  User
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { apiFetch } from '../../services/api_service';


/* ─── Severity & Status Maps ──────────────────────────────────────────────── */
const SEVERITY_STYLES = {
  low:      'text-[#6B6B6B] bg-[#1A1A1A] border-[#2A2A2A]',
  normal:   'text-[#A0A0A0] bg-[#1A1A1A] border-[#2A2A2A]',
  high:     'text-amber-400 bg-amber-500/10 border-amber-500/20',
  critical: 'text-red-400 bg-red-500/10 border-red-500/20',
};

const STATUS_ICON_COLORS = {
  'evidence.uploaded':        'text-cyan-400 bg-cyan-500/10',
  'evidence.approved':        'text-emerald-400 bg-emerald-500/10',
  'evidence.rejected':        'text-red-400 bg-red-500/10',
  'evidence.mapped':          'text-blue-400 bg-blue-500/10',
  'evidence.version_created': 'text-blue-400 bg-blue-500/10',
  'claim.created':            'text-emerald-400 bg-emerald-500/10',
  'claim.updated':            'text-blue-400 bg-blue-500/10',
  'claim.approved':           'text-emerald-400 bg-emerald-500/10',
  'disclosure.created':       'text-purple-400 bg-purple-500/10',
  'disclosure.stage_changed': 'text-blue-400 bg-blue-500/10',
  'disclosure.approved':      'text-emerald-400 bg-emerald-500/10',
  'obligation.created':       'text-amber-400 bg-amber-500/10',
  'obligation.assigned':      'text-blue-400 bg-blue-500/10',
  'obligation.completed':     'text-emerald-400 bg-emerald-500/10',
  'user.login':               'text-purple-400 bg-purple-500/10',
  'user.logout':              'text-[#6B6B6B] bg-[#1A1A1A]',
  'user.role_changed':        'text-amber-400 bg-amber-500/10',
  'report.exported':          'text-teal-400 bg-teal-500/10',
  'audit_pack.generated':     'text-indigo-400 bg-indigo-500/10',
  'system.config_changed':    'text-red-400 bg-red-500/10',
};

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'evidence.uploaded', label: 'Evidence Uploaded' },
  { value: 'evidence.approved', label: 'Evidence Approved' },
  { value: 'evidence.rejected', label: 'Evidence Rejected' },
  { value: 'evidence.mapped', label: 'Evidence Mapped' },
  { value: 'claim.created', label: 'Claim Created' },
  { value: 'claim.approved', label: 'Claim Approved' },
  { value: 'disclosure.created', label: 'Disclosure Created' },
  { value: 'disclosure.approved', label: 'Disclosure Approved' },
  { value: 'obligation.created', label: 'Obligation Created' },
  { value: 'obligation.completed', label: 'Obligation Completed' },
  { value: 'user.login', label: 'User Login' },
  { value: 'user.logout', label: 'User Logout' },
  { value: 'user.role_changed', label: 'Role Changed' },
  { value: 'report.exported', label: 'Report Exported' },
  { value: 'system.config_changed', label: 'Config Changed' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'All Severity' },
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
function formatAction(action) {
  if (!action) return 'Unknown';
  return action
    .replace(/\./g, ' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function timeAgo(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function getToken() { return localStorage.getItem('token'); }

/* ─── Main Page Component ─────────────────────────────────────────────────── */
export function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [stats, setStats] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);

  // UI
  const [selectedLog, setSelectedLog] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [seeding, setSeeding] = useState(false);

  /* ─── Fetch Logs ─────────────────────────────── */
  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (filterAction) params.append('action', filterAction);
      if (filterSeverity) params.append('severity', filterSeverity);
      if (search) params.append('search', search);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);

      const data = await apiFetch(`/v1/admin/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (data && !data._error) {
        setLogs(data.logs || []);
        setTotalCount(data.pagination?.totalCount || 0);
        setTotalPages(data.pagination?.totalPages || 1);
      }
    } catch (e) {
      console.error('Fetch audit logs failed:', e);
    } finally {
      setLoading(false);
    }
  }, [page, filterAction, filterSeverity, search, dateRange]);

  /* ─── Fetch Stats ────────────────────────────── */
  const fetchStats = useCallback(async () => {
    try {
      const res = await apiFetch(`/v1/admin/audit-logs/stats?days=30`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res && !res._error) setStats(res);
    } catch (e) {
      console.error('Fetch stats failed:', e);
    }
  }, []);

  useEffect(() => { fetchLogs(); fetchStats(); }, [fetchLogs, fetchStats]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => { fetchLogs(); fetchStats(); }, 5000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchLogs, fetchStats]);

  /* ─── Seed Demo Data ─────────────────────────── */
  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await apiFetch(`/v1/admin/audit-logs/seed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res && !res._error) {
        fetchLogs();
        fetchStats();
      }
    } catch (e) {
      console.error('Seed failed:', e);
    } finally {
      setSeeding(false);
    }
  };

  /* ─── CSV Export ─────────────────────────────── */
  const handleExport = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/v1/admin/audit-logs/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          filters: {
            action: filterAction || undefined,
            severity: filterSeverity || undefined,
            startDate: dateRange.start || undefined,
            endDate: dateRange.end || undefined,
          },
        }),
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error('Export failed:', e);
    }
  };

  const clearFilters = () => {
    setFilterAction('');
    setFilterSeverity('');
    setDateRange({ start: '', end: '' });
    setSearch('');
    setPage(1);
  };

  /* ─── Render ─────────────────────────────────── */
  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="audit-logs-title">Audit Logs</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Security &amp; compliance audit trail &middot; {totalCount.toLocaleString()} events
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
              autoRefresh
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                : 'bg-[#111] border-[#2A2A2A] text-[#6B6B6B] hover:border-[#3A3A3A]'
            }`}
            data-testid="auto-refresh-btn"
          >
            <Activity className={`w-3.5 h-3.5 ${autoRefresh ? 'animate-pulse' : ''}`} />
            {autoRefresh ? 'Live' : 'Auto-refresh'}
          </button>
          <button
            onClick={() => { fetchLogs(); fetchStats(); }}
            className="p-2 rounded-lg bg-[#111] border border-[#2A2A2A] text-[#6B6B6B] hover:text-white hover:border-[#3A3A3A] transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <Button
            onClick={handleExport}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
            data-testid="export-csv-btn"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-3" data-testid="audit-stats-cards">
        <StatCard
          icon={<Activity className="w-4 h-4" />}
          label="Total Events"
          value={stats?.totalEvents ?? totalCount}
          accent="emerald"
        />
        <StatCard
          icon={<Shield className="w-4 h-4" />}
          label="Critical"
          value={stats?.criticalEvents ?? 0}
          accent="red"
        />
        <StatCard
          icon={<AlertTriangle className="w-4 h-4" />}
          label="High Severity"
          value={stats?.severityCounts?.high ?? 0}
          accent="amber"
        />
        <StatCard
          icon={<Clock className="w-4 h-4" />}
          label="Last 24h"
          value={stats?.last24h ?? 0}
          accent="blue"
        />
      </div>

      {/* Toolbar */}
      <div className="bg-[#111] border border-[#1F1F1F] rounded-xl p-3" data-testid="audit-toolbar">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchLogs()}
              placeholder="Search by user, action, resource..."
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-[#4A4A4A] focus:border-emerald-500/40 focus:outline-none"
              data-testid="audit-search-input"
            />
          </div>

          <select
            value={filterAction}
            onChange={e => { setFilterAction(e.target.value); setPage(1); }}
            className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/40"
            data-testid="filter-action"
          >
            {ACTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <select
            value={filterSeverity}
            onChange={e => { setFilterSeverity(e.target.value); setPage(1); }}
            className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/40"
            data-testid="filter-severity"
          >
            {SEVERITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <button
            onClick={() => setShowFilters(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors ${
              showFilters
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                : 'border-[#2A2A2A] text-[#6B6B6B] hover:border-[#3A3A3A]'
            }`}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-3.5 h-3.5" />
            Date Range
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>

          {(filterAction || filterSeverity || dateRange.start || dateRange.end || search) && (
            <button onClick={clearFilters} className="text-xs text-[#6B6B6B] hover:text-white underline" data-testid="clear-filters-btn">
              Clear all
            </button>
          )}
        </div>

        {/* Date Range Panel */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t border-[#1F1F1F] grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#6B6B6B] mb-1">Start Date</label>
              <input
                type="datetime-local"
                value={dateRange.start}
                onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/40"
                data-testid="filter-start-date"
              />
            </div>
            <div>
              <label className="block text-xs text-[#6B6B6B] mb-1">End Date</label>
              <input
                type="datetime-local"
                value={dateRange.end}
                onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/40"
                data-testid="filter-end-date"
              />
            </div>
          </div>
        )}
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
        </div>
      ) : logs.length > 0 ? (
        <div className="bg-[#111] border border-[#1F1F1F] rounded-xl overflow-hidden" data-testid="audit-logs-table">
          {/* Table header */}
          <div className="grid grid-cols-[1fr_1.2fr_1.5fr_1fr_0.7fr_0.6fr_auto] gap-2 px-4 py-2.5 bg-[#0A0A0A] border-b border-[#1F1F1F] text-[10px] uppercase tracking-wider font-semibold text-[#4A4A4A]">
            <span>Timestamp</span>
            <span>Actor</span>
            <span>Action</span>
            <span>Resource</span>
            <span>Severity</span>
            <span>IP</span>
            <span />
          </div>

          {/* Rows */}
          <div className="divide-y divide-[#1A1A1A]">
            {logs.map((log, i) => (
              <LogRow key={log.id || log.event_id || i} log={log} onView={() => setSelectedLog(log)} testIndex={i} />
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-16" data-testid="empty-state">
          <Activity className="w-10 h-10 mx-auto mb-3 text-[#2A2A2A]" />
          <p className="text-[#6B6B6B] mb-4">No audit events found</p>
          <Button
            onClick={handleSeed}
            disabled={seeding}
            size="sm"
            variant="outline"
            className="border-[#2A2A2A] text-[#A0A0A0] hover:text-white gap-1.5"
            data-testid="seed-btn"
          >
            {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            Seed Sample Events
          </Button>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between" data-testid="pagination">
          <p className="text-xs text-[#6B6B6B]">
            Page {page} of {totalPages} &middot; {totalCount.toLocaleString()} events
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="border-[#2A2A2A]" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" className="border-[#2A2A2A]" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedLog && <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />}
    </div>
  );
}

/* ─── Stat Card ───────────────────────────────────────────────────────────── */
function StatCard({ icon, label, value, accent }) {
  const colors = {
    emerald: 'border-emerald-500/20 text-emerald-400',
    red:     'border-red-500/20 text-red-400',
    amber:   'border-amber-500/20 text-amber-400',
    blue:    'border-blue-500/20 text-blue-400',
  };
  return (
    <div className={`bg-[#111] border ${colors[accent]} rounded-xl p-3.5`} data-testid={`stat-${accent}`}>
      <div className="flex items-center justify-between mb-1">
        {icon}
        <span className="text-xl font-bold">{typeof value === 'number' ? value.toLocaleString() : value}</span>
      </div>
      <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">{label}</p>
    </div>
  );
}

/* ─── Log Row ─────────────────────────────────────────────────────────────── */
function LogRow({ log, onView, testIndex }) {
  const actor = log.actor || {};
  const eventType = log.event_type || '';
  const iconColor = STATUS_ICON_COLORS[eventType] || 'text-[#6B6B6B] bg-[#1A1A1A]';
  const sevStyle = SEVERITY_STYLES[log.event_severity] || SEVERITY_STYLES.normal;
  const isCritical = log.event_severity === 'critical';

  return (
    <div
      className={`grid grid-cols-[1fr_1.2fr_1.5fr_1fr_0.7fr_0.6fr_auto] gap-2 px-4 py-2.5 items-center hover:bg-[#151515] transition-colors ${isCritical ? 'bg-red-500/5' : ''}`}
      data-testid={`log-entry-${testIndex}`}
    >
      {/* Timestamp */}
      <div className="text-xs text-[#6B6B6B]">
        <div>{new Date(log.event_timestamp).toLocaleDateString()}</div>
        <div className="text-[10px] text-[#4A4A4A]">{timeAgo(log.event_timestamp)}</div>
      </div>

      {/* Actor */}
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center text-[10px] font-bold text-white shrink-0">
          {(actor.user_name || actor.user_email || '?')[0].toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="text-xs text-white font-medium truncate">{actor.user_name || 'System'}</p>
          <p className="text-[10px] text-[#4A4A4A] truncate">{actor.user_email || ''}</p>
        </div>
      </div>

      {/* Action */}
      <div className="flex items-center gap-2 min-w-0">
        <div className={`p-1.5 rounded-md ${iconColor} shrink-0`}>
          <Activity className="w-3 h-3" />
        </div>
        <span className="text-xs text-white truncate">{formatAction(eventType)}</span>
      </div>

      {/* Resource */}
      <div className="min-w-0">
        <p className="text-xs text-[#A0A0A0] capitalize">{log.aggregate_type || '-'}</p>
        <p className="text-[10px] text-[#4A4A4A] truncate">{log.aggregate_id || ''}</p>
      </div>

      {/* Severity */}
      <Badge className={`${sevStyle} border text-[10px] px-2 py-0.5 w-fit`}>
        {log.event_severity || 'normal'}
      </Badge>

      {/* IP */}
      <span className="text-[10px] text-[#4A4A4A] font-mono">{actor.ip_address || '-'}</span>

      {/* View */}
      <button
        onClick={onView}
        className="p-1.5 rounded-lg hover:bg-[#1F1F1F] transition-colors"
        data-testid={`view-detail-${testIndex}`}
      >
        <Eye className="w-3.5 h-3.5 text-[#6B6B6B] hover:text-white" />
      </button>
    </div>
  );
}

/* ─── Detail Modal ────────────────────────────────────────────────────────── */
function LogDetailModal({ log, onClose }) {
  const actor = log.actor || {};
  const data = log.event_data || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="log-detail-modal">
      <div
        className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#111] border-b border-[#1F1F1F] px-5 py-3.5 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-base font-bold text-white">Event Detail</h2>
            <p className="text-[10px] text-[#4A4A4A] mt-0.5 font-mono">{log.event_id || log.id}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#1F1F1F]" data-testid="close-modal-btn">
            <X className="w-4 h-4 text-[#6B6B6B]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Summary Grid */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Timestamp" value={log.event_timestamp ? new Date(log.event_timestamp).toLocaleString() : '-'} />
            <Field label="Event Type" value={formatAction(log.event_type)} />
            <Field label="Actor" value={`${actor.user_name || 'System'} (${actor.user_email || '-'})`} />
            <Field label="Role" value={actor.user_role || '-'} />
            <Field label="Resource" value={`${log.aggregate_type || '-'} / ${log.aggregate_id || '-'}`} />
            <Field label="Severity" value={log.event_severity || 'normal'} />
            <Field label="IP Address" value={actor.ip_address || '-'} />
            <Field label="Category" value={log.event_category || '-'} />
          </div>

          {/* Event Data */}
          {Object.keys(data).length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-[#6B6B6B] mb-2">Event Data</h3>
              <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg p-3 space-y-1">
                {Object.entries(data).map(([k, v]) => (
                  <div key={k} className="flex items-start gap-2 text-xs">
                    <span className="text-[#4A4A4A] shrink-0 w-32">{k}:</span>
                    <span className="text-white break-all">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {log.tags && log.tags.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-[#6B6B6B] mb-2">Tags</h3>
              <div className="flex flex-wrap gap-1.5">
                {log.tags.map((t, i) => (
                  <Badge key={i} className="bg-[#1A1A1A] border border-[#2A2A2A] text-[#A0A0A0] text-[10px]">{t}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Raw JSON */}
          <div>
            <h3 className="text-xs font-semibold text-[#6B6B6B] mb-2">Raw Event</h3>
            <pre className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg p-3 text-[10px] text-[#6B6B6B] overflow-x-auto max-h-48 font-mono">
              {JSON.stringify(log, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-[#4A4A4A] mb-0.5">{label}</p>
      <p className="text-xs text-white">{value}</p>
    </div>
  );
}

export default AuditLogsPage;
